import { Link, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Column,
  Grid,
  Icon,
  SIZE,
  Stack,
  Text,
  TIconName,
  ELEVATION,
  Skeleton,
} from '@quinyx/ui';

import styles from './home.module.css';
import { useUser } from '../context/authenticatedUser';
import {
  useDeletedCustomersQueue,
  usePaginatedCustomers,
  useRecentlyVisitedCustomers,
  useCustomerCreationsInProgress,
} from '../hooks/customer';
import { DELETED_CUSTOMER_QUEUE_STATUS } from '../constants/customer-deletion';
import { useTranslate } from '../hooks/translate';

const StatCard = ({
  title,
  value,
  icon,
  iconColor,
  link,
  isLoading,
}: {
  title: string;
  value: number;
  icon: TIconName;
  iconColor?: any;
  link?: string;
  isLoading?: boolean;
}) => {
  const { selectedLanguage } = useTranslate();
  const cardId = `home-stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`;
  const content = (
    <Stack
      data-test-id={cardId}
      direction="row"
      alignItems="center"
      spacing={SIZE.M}
      backgroundColor="SurfaceMain"
      borderRadius={SIZE.L}
      elevation={ELEVATION.SMOOTH}
      p={SIZE.M}>
      <Avatar icon={icon} variant={iconColor} type="alt" />
      <Stack spacing={SIZE.XS}>
        <Text variant="smallRegular">{title}</Text>
        {isLoading ? (
          <Skeleton type="round" width="100px" height="28px" />
        ) : (
          <Text variant="headlineHeavy" color="PrimaryMain">
            {value.toLocaleString(selectedLanguage)}
          </Text>
        )}
      </Stack>
    </Stack>
  );

  if (link) {
    return (
      <Link
        data-test-id={`${cardId}-link`}
        to={link}
        style={{ textDecoration: 'none' }}>
        {content}
      </Link>
    );
  }

  return content;
};

const QuickActionCard = ({
  title,
  description,
  icon,
  link,
  buttonText,
}: {
  title: string;
  description: string;
  icon: TIconName;
  link: string;
  buttonText: string;
}) => {
  const navigate = useNavigate();
  const actionId = `home-quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <Stack
      data-test-id={actionId}
      spacing={SIZE.M}
      backgroundColor="SurfaceMain"
      borderRadius={SIZE.L}
      elevation={ELEVATION.SMOOTH}
      p={SIZE.M}
      style={{ height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={SIZE.S}>
        <Avatar icon={icon} variant="monsoon" type="alt" />
        <Text variant="headlineRegular" color="PrimaryMain">
          {title}
        </Text>
      </Stack>
      <Text variant="bodyRegular">{description}</Text>
      <Box style={{ marginTop: 'auto' }}>
        <Button
          data-test-id={`quick-action-button-${title.toLowerCase().replace(/\s+/g, '-')}`}
          variant="outlined"
          text={buttonText}
          onClick={() => {
            navigate(link);
          }}
        />
      </Box>
    </Stack>
  );
};

const SectionContainer = ({
  children,
  title,
  testId,
}: {
  children: React.ReactNode;
  title?: string;
  testId?: string;
}) => (
  <Stack
    data-test-id={testId}
    spacing={SIZE.M}
    backgroundColor="SurfaceContrastLeast"
    borderRadius={SIZE.XL}
    p={SIZE.M}>
    {title && (
      <Text variant="headlineHeavy" color="PrimaryMain">
        {title}
      </Text>
    )}
    {children}
  </Stack>
);

const PendingCreationsCard = () => {
  const { t } = useTranslate();
  const { data: customerCreationQueue = [], isLoading } =
    useCustomerCreationsInProgress();
  const customerCreationsInProgress = customerCreationQueue.filter(
    (creation) => creation.currentStatus !== 'done',
  );
  const pendingCreations = customerCreationsInProgress.length;

  return (
    <StatCard
      title={t('home.pendingCreations')}
      value={pendingCreations}
      icon="clock"
      iconColor="warning"
      link="/customers/create/in-progress"
      isLoading={isLoading}
    />
  );
};

const DeletionQueueCard = () => {
  const { t } = useTranslate();
  const { data: deletedCustomersQueue = [], isLoading } =
    useDeletedCustomersQueue();
  const queueItems = deletedCustomersQueue.length;

  return (
    <StatCard
      title={t('home.deletionQueue')}
      value={queueItems}
      icon="delete"
      iconColor="danger"
      link="/customers/queue"
      isLoading={isLoading}
    />
  );
};

const FailedDeletionsCard = () => {
  const { t } = useTranslate();
  const { data: deletedCustomersQueue = [], isLoading } =
    useDeletedCustomersQueue(DELETED_CUSTOMER_QUEUE_STATUS.FAILED);
  const failedDeletions = deletedCustomersQueue.length;

  return (
    <StatCard
      title={t('home.failedDeletions')}
      value={failedDeletions}
      icon="close"
      iconColor="red"
      isLoading={isLoading}
    />
  );
};

const Home = () => {
  const { t } = useTranslate();
  const { firstname, systemAdmin } = useUser();
  const { paginatedCustomers, isLoading: isLoadingPaginatedCustomers } =
    usePaginatedCustomers({ hideReadyForDeletion: true });
  const { recentCustomers } = useRecentlyVisitedCustomers();
  const navigate = useNavigate();

  return (
    <Box p={SIZE.XL} className={styles.homeContainer} data-test-id="home-page">
      <Stack spacing={SIZE.M}>
        {/* Welcome Section */}
        <Stack spacing={SIZE.S} data-test-id="home-welcome-section">
          <Text
            color="PrimaryMain"
            variant="titleLarge"
            data-test-id="home-welcome-title">
            {t('home.welcome', { name: firstname })}
          </Text>
          <Text variant="bodyRegular" data-test-id="home-welcome-subtitle">
            {t('home.welcomeSubtitle')}
          </Text>
        </Stack>

        {/* Stats Grid */}
        <SectionContainer testId="home-stats-section">
          <Grid columns={12} gap={SIZE.M}>
            <Column span={[12, 6, 3]}>
              <StatCard
                title={t('home.totalCustomers')}
                value={paginatedCustomers?.totalElements ?? 0}
                icon="customer"
                iconColor="primary"
                link="/customers"
                isLoading={isLoadingPaginatedCustomers}
              />
            </Column>
            <Column span={[12, 6, 3]}>
              {systemAdmin && <PendingCreationsCard />}
            </Column>
            <Column span={[12, 6, 3]}>
              {systemAdmin && <DeletionQueueCard />}
            </Column>
            <Column span={[12, 6, 3]}>
              {systemAdmin && <FailedDeletionsCard />}
            </Column>
          </Grid>
        </SectionContainer>

        {/* Quick Actions */}
        <SectionContainer
          title={t('home.quickActions')}
          testId="home-quick-actions-section">
          <Grid columns={12} gap={SIZE.M}>
            {systemAdmin && (
              <>
                <Column span={[12, 6, 4]}>
                  <QuickActionCard
                    title={t('home.createSysAdmin.title')}
                    description={t('home.createSysAdmin.description')}
                    icon="group"
                    link="/admins/create"
                    buttonText={t('home.createSysAdmin.button')}
                  />
                </Column>
                <Column span={[12, 6, 4]}>
                  <QuickActionCard
                    title={t('home.createAdminToken.title')}
                    description={t('home.createAdminToken.description')}
                    icon="link"
                    link="/admins/system-admin-token"
                    buttonText={t('home.createAdminToken.button')}
                  />
                </Column>
              </>
            )}
            <Column span={[12, 6, 4]}>
              <QuickActionCard
                title={t('home.createCustomer.title')}
                description={t('home.createCustomer.description')}
                icon="customer"
                link="/customers/create"
                buttonText={t('home.createCustomer.button')}
              />
            </Column>
          </Grid>
        </SectionContainer>

        {/* Recently Visited Customers */}
        {recentCustomers.length > 0 && (
          <SectionContainer
            title={t('home.recentlyVisitedCustomers')}
            testId="home-recent-customers-section">
            <Grid columns={12} gap={SIZE.S}>
              {recentCustomers.map((customer) => (
                <Column key={customer.customerId} span={[12, 6, 4]}>
                  <Stack
                    data-test-id={`home-recent-customer-${customer.customerId}`}
                    direction="row"
                    alignItems="center"
                    spacing={SIZE.M}
                    backgroundColor="SurfaceMain"
                    borderRadius={SIZE.L}
                    elevation={ELEVATION.SMOOTH}
                    p={SIZE.M}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigate(`/customers/${customer.customerId}`);
                    }}>
                    <Avatar name={customer.name} variant="primary" type="alt" />
                    <Stack spacing={SIZE.XS} style={{ flex: 1 }}>
                      <Text variant="bodyHeavy" color="PrimaryMain">
                        {customer.name}
                      </Text>
                      <Text variant="smallRegular">
                        {t('home.domainId', { id: customer.customerId })}
                      </Text>
                    </Stack>
                    <Icon icon="arrowRight" />
                  </Stack>
                </Column>
              ))}
            </Grid>
          </SectionContainer>
        )}
      </Stack>
    </Box>
  );
};

export default Home;
